import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Flex,
  Text,
  Button,
  Spinner
} from '@chakra-ui/react';
import { ContentSubmissionUnit, ContentRequirement } from '@foodfluence/shared';
import { formatFileSize, isImageFile, isVideoFile } from '../../../utils/contentUploadUtils';
import { ff } from '../../../theme';
import { StatusPill } from './submissionSheetParts';

const SMALL_BUTTON = { h: '44px', px: '16px', fontSize: '14px' } as const;

interface ContentPreviewProps {
  contentKey: string;
  index: number;
  contentType: 'image' | 'video' | 'none';
  previewUrl?: string;
  fileSize?: number;
  status?: ContentSubmissionUnit['status'];
  feedback?: string;
  isLoadingContent: boolean;
  onRemove: (contentKey: string) => void;
  onReplace: (contentKey: string) => void;
  isCarousel?: boolean;
}

export const ContentPreview: React.FC<ContentPreviewProps> = ({
  contentKey,
  index,
  contentType,
  previewUrl,
  fileSize,
  status,
  feedback,
  isLoadingContent,
  onRemove,
  onReplace,
  isCarousel = false
}) => {
  const renderPreviewContent = () => {
    if (isLoadingContent) {
      return (
        <VStack justify="center" align="center" h="100%">
          <Spinner size="md" color={ff.ink} />
          <Text fontSize="13px" color={ff.text2}>Loading...</Text>
        </VStack>
      );
    }

    if (!previewUrl || contentType === 'none') {
      return (
        <VStack justify="center" align="center" h="100%" bg={ff.grey}>
          <Text fontSize="13px" color={ff.muted}>No content</Text>
        </VStack>
      );
    }

    if (contentType === 'video') {
      return (
        <video 
          src={previewUrl}
          controls
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      );
    }

    return (
      <img 
        src={previewUrl}
        alt={`Content ${index + 1}`}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  };

  return (
    <Box
      border="1px solid"
      borderColor={ff.border}
      p="12px"
      borderRadius="20px"
      w="100%"
      bg="white"
      position="relative"
      opacity={status === 'voided' ? 0.6 : 1}
    >
      <VStack spacing="12px" align="stretch">
        {isCarousel && (
          <Text fontSize="12px" fontWeight={600} letterSpacing="0.08em" textTransform="uppercase" color={ff.text2}>
            Photo {index + 1}
          </Text>
        )}

        <Box w="100%" h="240px" borderRadius="18px" overflow="hidden" bg={ff.grey}>
          {renderPreviewContent()}
        </Box>

        {fileSize ? (
          // Show file controls for new uploads
          <Flex align="center" gap="8px" wrap="wrap">
            <Text flex={1} minW={0} fontSize="13px" color={ff.text2}>
              Size: {formatFileSize(fileSize)} MB
            </Text>
            <Button
              variant="inkOutline"
              {...SMALL_BUTTON}
              color={ff.danger}
              borderColor={ff.danger}
              onClick={() => onRemove(contentKey)}
            >
              Remove
            </Button>
            <Button
              variant="inkOutline"
              {...SMALL_BUTTON}
              onClick={() => onReplace(contentKey)}
            >
              Replace
            </Button>
          </Flex>
        ) : previewUrl ? (
          // Show existing content controls based on status
          <HStack spacing="8px" justify="space-between">
            {/*
              A voided unit is one the brand withdrew: the creator no longer owes it. Without its
              own branch it fell through to the else below and showed "Pending Review" plus a
              Replace button, inviting a resubmission of content nobody will look at. Matches
              ContentUnitCard's and DeliverableCard's copy.
            */}
            {status === 'voided' ? (
              <StatusPill tone="grey">
                Removed by the brand — no longer needed
              </StatusPill>
            ) : status === 'pendingSubmission' ? (
              <>
                <VStack align="stretch" spacing="8px" w="100%">
                  <Flex align="center" justify="space-between" gap="8px">
                    <StatusPill tone="red">
                      Needs resubmission
                    </StatusPill>
                    <Button
                      variant="ink"
                      {...SMALL_BUTTON}
                      onClick={() => onReplace(contentKey)}
                    >
                      Submit content
                    </Button>
                  </Flex>
                  {status === 'pendingSubmission' && feedback && (
                    <Box px="12px" py="10px" borderRadius="14px" bg="#FBE1DC" color="#6E1A0A" fontSize="14px" lineHeight={1.4}>
                      <Text as="strong" fontWeight={600}>Feedback:</Text> {feedback}
                    </Box>
                  )}
                </VStack>
              </>
            ) : (
              <>
                <StatusPill tone={status === 'approved' ? 'green' : 'grey'}>
                  {status === 'approved' ? 'Approved' : 'In review'}
                </StatusPill>
                {status !== 'approved' && (
                  <Button
                    variant="inkOutline"
                    {...SMALL_BUTTON}
                    onClick={() => onReplace(contentKey)}
                  >
                    Replace
                  </Button>
                )}
              </>
            )}
          </HStack>
        ) : null}
      </VStack>
    </Box>
  );
};
